import {
  AcceptPillarsDto,
  AgentDto,
  AgentVersionDto,
  ApproveClaimDto,
  BriefDto,
  ClaimDto,
  ConfirmSectionDto,
  IdeaDto,
  MakeIdeaDto,
  ResearchSettingsDto,
  SaveInstructionsDto,
  SetupDto,
  StartSetupDto,
  StartTrialDto,
  TrialDto,
} from "@growth/dto";
import { ContentFormat, IdeaStatus, KnowledgeKind, ResearchSettings } from "@growth/schema";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./api";

// ─── Agents ──────────────────────────────────────────────────────────────────────────────────

export const useAgents = () =>
  useQuery({
    queryKey: ["agents"],
    queryFn: async () => (await api.get<AgentDto[]>("/agents")).data,
  });

export const useAgentVersions = (key: string, enabled: boolean) =>
  useQuery({
    queryKey: ["agents", key, "versions"],
    queryFn: async () => (await api.get<AgentVersionDto[]>(`/agents/${key}/versions`)).data,
    enabled,
  });

const useAgentRefresh = () => {
  const queryClient = useQueryClient();
  return (agent: AgentDto) => {
    queryClient.setQueryData<AgentDto[]>(["agents"], (list: AgentDto[] | undefined) =>
      list?.map((item: AgentDto) => (item.key === agent.key ? agent : item)),
    );
    void queryClient.invalidateQueries({ queryKey: ["agents", agent.key, "versions"] });
  };
};

export const useSaveInstructions = (key: string) => {
  const refresh = useAgentRefresh();
  return useMutation({
    mutationFn: async (input: SaveInstructionsDto) =>
      (await api.put<AgentDto>(`/agents/${key}/instructions`, input)).data,
    onSuccess: refresh,
  });
};

export const useResetInstructions = (key: string) => {
  const refresh = useAgentRefresh();
  return useMutation({
    mutationFn: async (baseVersion: number) =>
      (await api.post<AgentDto>(`/agents/${key}/reset`, { baseVersion })).data,
    onSuccess: refresh,
  });
};

export const useStartTrial = (key: string) =>
  useMutation({
    mutationFn: async (input: StartTrialDto) =>
      (await api.post<TrialDto>(`/agents/${key}/trials`, input)).data,
  });

export const useTrial = (id: string | null) =>
  useQuery({
    queryKey: ["trials", id],
    queryFn: async () => (await api.get<TrialDto>(`/agents/trials/${id}`)).data,
    enabled: Boolean(id),
    refetchInterval: (query) =>
      !query.state.data || ["queued", "running"].includes(query.state.data.status) ? 1500 : false,
  });

// ─── Brand setup ─────────────────────────────────────────────────────────────────────────────

const busy = (setup: SetupDto | undefined) =>
  setup?.status === "running" ||
  setup?.look?.status === "running" ||
  Boolean(setup?.sections.some((section) => section.state === "redoing"));

/** Polls while an agent works on setup; `every` is faster on the welcome screen. */
export const useSetup = (every = 2500) =>
  useQuery({
    queryKey: ["setup"],
    queryFn: async () => (await api.get<SetupDto>("/research/setup")).data,
    refetchInterval: (query) => (busy(query.state.data) ? every : false),
  });

const useSetupMutation = <T>(fn: (input: T) => Promise<SetupDto>) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (setup) => {
      queryClient.setQueryData(["setup"], setup);
      void queryClient.invalidateQueries({ queryKey: ["knowledge"] });
      void queryClient.invalidateQueries({ queryKey: ["claims"] });
      void queryClient.invalidateQueries({ queryKey: ["content-settings"] });
      void queryClient.invalidateQueries({ queryKey: ["research-settings"] });
    },
  });
};

export const useStartSetup = () =>
  useSetupMutation(
    async (input: StartSetupDto) => (await api.post<SetupDto>("/research/setup", input)).data,
  );

/** The brand designer reads the website's look again. */
export const useStartLook = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => (await api.post("/research/look")).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["setup"] }),
  });
};

/** "Use this look": the found colours, type and logo become the brand kit. */
export const useApplyLook = () =>
  useSetupMutation(async () => (await api.post<SetupDto>("/research/look/apply")).data);

export const useConfirmSection = () =>
  useSetupMutation(
    async ({ kind, ...input }: ConfirmSectionDto & { kind: KnowledgeKind }) =>
      (await api.post<SetupDto>(`/research/setup/sections/${kind}/confirm`, input)).data,
  );

export const useDiscardDraft = () =>
  useSetupMutation(
    async (kind: KnowledgeKind) =>
      (await api.post<SetupDto>(`/research/setup/sections/${kind}/discard`)).data,
  );

export const useRedoSection = () =>
  useSetupMutation(
    async ({ kind, note }: { kind: KnowledgeKind; note: string }) =>
      (await api.post<SetupDto>(`/research/setup/sections/${kind}/redo`, { note })).data,
  );

export const useAcceptPillars = () =>
  useSetupMutation(
    async (input: AcceptPillarsDto) =>
      (await api.post<SetupDto>("/research/setup/pillars", input)).data,
  );

// ─── Suggested claims ────────────────────────────────────────────────────────────────────────

const useClaimMutation = <T>(fn: (input: T) => Promise<ClaimDto>) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["claims"] });
      void queryClient.invalidateQueries({ queryKey: ["setup"] });
    },
  });
};

export const useApproveClaim = () =>
  useClaimMutation(
    async ({ id, ...input }: ApproveClaimDto & { id: string }) =>
      (await api.post<ClaimDto>(`/claims/${id}/approve`, input)).data,
  );

export const useRejectClaim = () =>
  useClaimMutation(
    async ({ id, reason }: { id: string; reason: string }) =>
      (await api.post<ClaimDto>(`/claims/${id}/reject`, { reason })).data,
  );

// ─── Research ────────────────────────────────────────────────────────────────────────────────

export const useResearchSettings = () =>
  useQuery({
    queryKey: ["research-settings"],
    queryFn: async () => (await api.get<ResearchSettingsDto>("/research/settings")).data,
  });

export const useSaveResearchSettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: ResearchSettings) =>
      (await api.put<ResearchSettingsDto>("/research/settings", input)).data,
    onSuccess: (settings) => queryClient.setQueryData(["research-settings"], settings),
  });
};

export const useBriefs = () =>
  useQuery({
    queryKey: ["briefs"],
    queryFn: async () => (await api.get<BriefDto[]>("/research/briefs")).data,
    refetchInterval: (query) =>
      query.state.data?.some((brief) => brief.status === "running") ? 3000 : false,
  });

export const useStartBrief = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (focus: string) =>
      (await api.post<BriefDto>("/research/briefs", { focus })).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["briefs"] }),
  });
};

export const useIdeas = (status?: IdeaStatus) =>
  useQuery({
    queryKey: ["ideas", status ?? "all"],
    queryFn: async () =>
      (await api.get<IdeaDto[]>("/ideas", { params: status ? { status } : {} })).data,
  });

type IdeaAction =
  | { kind: "save"; id: string }
  | { kind: "restore"; id: string }
  | { kind: "dismiss"; id: string; reason: string }
  | { kind: "make"; id: string; formats: ContentFormat[] };

export const useIdeaAction = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (action: IdeaAction) => {
      if (action.kind === "make") {
        const input: MakeIdeaDto = { formats: action.formats };
        return (
          await api.post<{ idea: IdeaDto; pieceIds: string[] }>(`/ideas/${action.id}/make`, input)
        ).data;
      }
      const body = action.kind === "dismiss" ? { reason: action.reason } : {};
      return {
        idea: (await api.post<IdeaDto>(`/ideas/${action.id}/${action.kind}`, body)).data,
        pieceIds: [] as string[],
      };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["ideas"] });
      void queryClient.invalidateQueries({ queryKey: ["briefs"] });
      void queryClient.invalidateQueries({ queryKey: ["pieces"], exact: true });
    },
  });
};
